/**
 * Der Projekt-Dialog: Name, überwachter Pfad, PHP-Versionen, Startrevision —
 * dazu die Betriebsschau auf den Poller.
 *
 * Ein Projekt ist ein **überwachter Unterbaum des SVN-Repositories**, kein
 * eigenes Repository; alle Projekte liegen derzeit im selben. Die Startrevision
 * verhindert, dass die erste Abholung die gesamte Historie einzieht.
 *
 * Angehalten wird ein Projekt, gelöscht wird keines: An ihm hängen Revisionen
 * und Befunde.
 *
 * **Die Startrevision ist der eine Wert dieser Maske, der Fortschritt
 * vernichtet.** Solange der Poller nie lief, gibt es dabei nichts zu verlieren.
 * Danach ist ihre Änderung ausdrücklich als Rücksetzen des Fortschritts
 * kenntlich gemacht und wird bestätigt — hier und im Server, denn eine Prüfung,
 * die nur die Oberfläche einhält, ist keine.
 *
 * **Die laufende PHP-Version ist Pflicht, die geplanten Zielversionen sind es
 * nicht.** Zwei Felder und nicht eines, weil sie verschiedene Leser haben: Die
 * Kontrolle arbeitet gegen die laufende und erklärte ohne sie gültigen Code für
 * kaputt; der Grundtext nennt dem Modell beide, damit die Befundart `migration`
 * eine Bezugsgröße hat. Eine Liste ohne Auszeichnung, welche die laufende ist,
 * nützt der Kontrolle nichts.
 *
 * **Von hier wird der Bestandslauf angestoßen** — eine Kontrolle über alle
 * PHP-Dateien des überwachten Unterbaums statt nur über die geänderten. Sie
 * erreicht damit den Altbestand, den der Revisionsstrom nie zu Gesicht bekommt.
 * Er steht in keinem Cron und wird bestätigt: Ein Bestandslauf wird bewusst
 * angestoßen und legt je Datei einen Auftrag an.
 *
 * **Das Ticketsystem steht hier nicht** (#112). Wohin ein Ticketverweis aus dem
 * Commit-Kommentar zeigt, sagt seit der Korrektur an #101 die Zuordnungsregel und
 * nicht das Projekt: Beide Ticketlisten gelten für alle Projekte, und zwei Wege,
 * dasselbe Ziel zu bestimmen, sind die Doppelpflege, die später auseinanderläuft.
 * Gepflegt wird das im Abschnitt „Ticketsystem".
 *
 * **Die Anzeigeschwelle ist eine Anzeigeschwelle** (Spec v1.3, Story 241): Sie
 * setzt die Vorbelegung des Filters im Befundvorrat und sonst nichts. Erhoben
 * und gespeichert wird weiter alles, und sie greift weder in die Erhebung noch
 * in die Prüfaufträge ein — wer hier Befunde wegwürfe, nähme sich genau die
 * Daten, mit denen sich später kalibrieren ließe. Deshalb steht sie in der Liste
 * und nicht nur im Dialog: Eine Schwelle, von der niemand weiß, verschweigt
 * still.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import {
  Button,
  Checkbox,
  Chip,
  DataTable,
  Dialog,
  FormGrid,
  FormRow,
  IconButton,
  Modal,
  Select,
  StatusPill,
  TextField,
} from "../../design-system";
import type { AdminApi, InventoryRun, Project, ProjectDraft, Severity } from "../../api/client";
import { formatNumber, formatTimestamp } from "../../formatting";
import { SEVERITY_TONES } from "../findings";
import { NumericCell, StrongCell } from "../cells";
import { ListSection } from "../list";
import { Notice, describe, useLoaded } from "../loading";

/**
 * Die Schweregrade als Auswahl, von leicht nach schwer — die Reihenfolge einer
 * Schwelle: Je weiter unten, desto mehr bleibt still.
 */
const SEVERITIES: Severity[] = ["hinweis", "mittel", "schwer"];

export function ProjectsSection({ client }: { client: AdminApi }): ReactElement {
  const { state, reload } = useLoaded(() => client.projects());
  const projects = state.kind === "loaded" ? state.value.projects : [];
  const [edited, setEdited] = useState<Project | "neu" | null>(null);
  const [inventoried, setInventoried] = useState<Project | null>(null);
  const [started, setStarted] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const act = (action: Promise<unknown>): void => {
    setFailure(null);
    action.then(reload, (error: unknown) => setFailure(describe(error)));
  };

  return (
    <>
      <ListSection
        title="Projekte"
        glyph="folder-git-2"
        first
        headerRight={
          // Der Knopf zum Anlegen stand am Fuß der Karte; ein Abschnitt hat
          // keinen Fuß, und ein Schalter über die ganze Fensterbreite wäre
          // keiner. Er steht deshalb neben dem Abzeichen im Kopf.
          <span style={{ display: "flex", alignItems: "center", gap: "var(--sp-5)" }}>
            {state.kind === "loaded" ? <Chip>{`${projects.length} überwacht`}</Chip> : null}
            <Button variant="outline" icon="plus" onClick={() => setEdited("neu")}>
              Projekt anlegen
            </Button>
          </span>
        }
      >
        <div style={{ padding: "var(--sp-2) var(--sp-5)" }}>
          {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {state.kind === "failed" ? (
            <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {failure === null ? null : (
            <Notice text={failure} glyph="triangle-alert" color="var(--ac-red)" />
          )}
          {started === null ? null : <Notice text={started} glyph="scan-search" />}
          {state.kind === "loaded" && projects.length === 0 ? (
            <Notice text="Kein Projekt überwacht." />
          ) : null}
        </div>

        {state.kind === "loaded" && projects.length > 0 ? (
          <DataTable
            columns={[
              { key: "name", label: "Name", width: 180, maxWidth: 180 },
              // Ein überwachter Unterbaum wird von hinten gelesen — der Pfad
              // bricht hinten ab und steht ganz im Titel der Zelle.
              { key: "path", label: "Überwachter Pfad", width: 260, maxWidth: 260 },
              { key: "php", label: "PHP", width: 150 },
              { key: "start", label: "Startrevision", align: "right", width: 110 },
              { key: "processed", label: "Zuletzt verarbeitet", align: "right", width: 140 },
              { key: "threshold", label: "Anzeigeschwelle", width: 130 },
              { key: "poll", label: "Letzter Pollerlauf", width: 220 },
              { key: "state", label: "Zustand", width: 110 },
              { key: "actions", label: "", width: 70, align: "right" },
            ]}
            rows={projects.map((project) => ({
              cells: {
                name: <StrongCell>{project.name}</StrongCell>,
                path: <span title={project.svnPath}>{project.svnPath}</span>,
                php: <PhpVersionsCell project={project} />,
                start: <NumericCell>{formatNumber(project.startRevision)}</NumericCell>,
                processed: (
                  <NumericCell>{formatNumber(project.lastProcessedRevision)}</NumericCell>
                ),
                threshold: <ThresholdCell severity={project.displayThreshold} />,
                poll: <PollCell project={project} />,
                state: project.active ? (
                  <StatusPill tone="green">aktiv</StatusPill>
                ) : (
                  <Chip>angehalten</Chip>
                ),
                actions: (
                  <span style={{ display: "inline-flex", gap: "var(--sp-3)" }}>
                    {project.active ? (
                      <IconButton
                        icon="pause"
                        tip="Anhalten"
                        size="sm"
                        tone="quiet"
                        onClick={() => act(client.pauseProject(project.id))}
                      />
                    ) : (
                      <IconButton
                        icon="play"
                        tip="Wieder aufnehmen"
                        size="sm"
                        tone="quiet"
                        onClick={() => act(client.resumeProject(project.id))}
                      />
                    )}
                    <IconButton
                      icon="scan-search"
                      tip="Bestandslauf anstoßen"
                      size="sm"
                      tone="quiet"
                      onClick={() => {
                        setStarted(null);
                        setInventoried(project);
                      }}
                    />
                    <IconButton
                      icon="pencil"
                      tip="Ändern"
                      size="sm"
                      tone="quiet"
                      onClick={() => setEdited(project)}
                    />
                  </span>
                ),
              },
            }))}
          />
        ) : null}
      </ListSection>

      {inventoried === null ? null : (
        <InventoryDialog
          project={inventoried}
          start={() => client.startInventoryRun(inventoried.id)}
          close={() => setInventoried(null)}
          started={(run) => {
            setInventoried(null);
            setStarted(describeRun(run));
          }}
        />
      )}

      {edited === null ? null : (
        <ProjectDialog
          project={edited === "neu" ? null : edited}
          save={(draft) =>
            edited === "neu" ? client.createProject(draft) : client.updateProject(edited.id, draft)
          }
          close={() => setEdited(null)}
          saved={() => {
            setEdited(null);
            reload();
          }}
        />
      )}
    </>
  );
}

/**
 * Ab welchem Grad ein Projekt seine Befunde zeigt.
 *
 * „hinweis" trägt keine Farbe — es ist die Schwelle, die nichts zurückhält, und
 * damit kein Zustand, sondern eine Angabe. Die beiden anderen sind Zustände.
 */
function ThresholdCell({ severity }: { severity: Severity }): ReactElement {
  const tone = SEVERITY_TONES[severity];
  const label = `ab ${severity}`;

  return tone === null ? <Chip>{label}</Chip> : <StatusPill tone={tone}>{label}</StatusPill>;
}

/**
 * Zeitpunkt und Ergebnis des letzten Pollerlaufs — ein stehender Poller soll
 * auffallen, bevor jemand ihn vermisst. Ein Projekt, für das er nie lief, sagt
 * das ausdrücklich und steht nicht mit leerer Spalte da.
 */
function PollCell({ project }: { project: Project }): ReactElement {
  if (project.lastPollAt === null) {
    return <span style={{ color: "var(--fg-6)" }}>noch nicht gelaufen</span>;
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-4)" }}>
      <span>{formatTimestamp(project.lastPollAt)}</span>
      {project.lastPollResult === null ? null : (
        <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
          {project.lastPollResult}
        </span>
      )}
    </span>
  );
}

/**
 * Die laufende PHP-Version, gegen die die Kontrolle arbeitet — und daneben, was
 * geplant ist. Die geplanten Zielversionen stehen leiser: Sie sind eine Absicht,
 * die laufende ist eine Tatsache. Ein Projekt ohne Plan bleibt an dieser Stelle
 * still und steht nicht mit einem Platzhalter da.
 */
function PhpVersionsCell({ project }: { project: Project }): ReactElement {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-4)" }}>
      <span>{project.currentPhpVersion}</span>
      {project.plannedPhpVersions.length === 0 ? null : (
        <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
          {`geplant: ${project.plannedPhpVersions.join(", ")}`}
        </span>
      )}
    </span>
  );
}

/**
 * Was ein angestoßener Bestandslauf angelegt hat — in einem Satz.
 *
 * Der Lauf ohne Muster ist der Normalfall des zweiten Anstoßes und **keine
 * Störung**: Ein Muster, das den Altbestand schon gesehen hat, ein zweites Mal
 * darüber zu schicken erzeugte bei unverändertem Code dieselben Funde an einer
 * neueren Revisionsnummer — also doppelte Befunde. Die Maske sagt das, statt
 * eine leere Rückmeldung zu geben, die wie ein Fehler aussieht.
 */
function describeRun(run: InventoryRun): string {
  if (run.patterns.length === 0) {
    return (
      "Kein neues Kontrollmuster seit dem letzten Bestandslauf: Es gibt nichts zu tun. "
      + "Ein weiterer Lauf verarbeitet nur die seither hinzugekommenen Muster."
    );
  }

  const files
    = run.files === 1 ? "eine PHP-Datei" : `${formatNumber(run.files)} PHP-Dateien`;

  return (
    `Bestandslauf angestoßen: ${files} gegen Revision ${formatNumber(run.revisionNumber)}, `
    + `${run.patterns.length === 1 ? "ein Kontrollmuster" : `${run.patterns.length} Kontrollmuster`}. `
    + "Die Aufträge stehen nachrangig in der Warteschlange und bremsen die Begutachtung nicht aus."
  );
}

/**
 * Der Anstoß, ausdrücklich bestätigt. Er legt je PHP-Datei des überwachten
 * Unterbaums einen Auftrag an — bei einem großen Baum sind das viele —, und die
 * Revisionsnummer wird dabei einmal aufgelöst und nicht als `HEAD` gemerkt.
 */
function InventoryDialog({
  project,
  start,
  close,
  started,
}: {
  project: Project;
  start: () => Promise<InventoryRun>;
  close: () => void;
  started: (run: InventoryRun) => void;
}): ReactElement {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (): void => {
    setBusy(true);
    setError(null);

    start().then(started, (failure: unknown) => {
      setBusy(false);
      setError(describe(failure));
    });
  };

  return (
    <Modal onClose={close}>
      <Dialog
        title="Bestandslauf anstoßen"
        description="Eine Kontrolle über alle PHP-Dateien des überwachten Unterbaums statt nur über die geänderten. Sie erreicht damit die Dateien, die seit der Startrevision niemand angefasst hat."
        width={560}
        onClose={close}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={close}>
              Abbrechen
            </Button>
            <Button variant="primary" size="lg" disabled={busy} onClick={submit}>
              {busy ? "Wird angestoßen ..." : "Anstoßen"}
            </Button>
          </>
        }
      >
        <FormGrid labelWidth="var(--grid-label-col)">
          <FormRow label="Projekt">
            <span>{`${project.name} — ${project.svnPath}`}</span>
          </FormRow>

          <FormRow label="Stand">
            <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
              {`Kontrolliert wird der Stand von Revision ${formatNumber(project.lastProcessedRevision)}; `
                + "die Nummer wird beim Start einmal aufgelöst, damit alle Befunde eines Laufs "
                + "auf denselben Stand zeigen."}
            </span>
          </FormRow>

          <FormRow label="">
            <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
              Je Datei entsteht ein Kontrollauftrag. Er läuft ohne Endpunkt und ohne Slot und steht
              nachrangig zur laufenden Begutachtung. Ein Modell wird dabei nicht gerufen.
            </span>
          </FormRow>

          {error === null ? null : (
            <FormRow full>
              <Notice text={error} glyph="triangle-alert" color="var(--ac-red)" />
            </FormRow>
          )}
        </FormGrid>
      </Dialog>
    </Modal>
  );
}

/** Die geplanten Zielversionen als eine Zeile, so wie der Dialog sie anzeigt. */
function versionsAsText(versions: string[]): string {
  return versions.join(", ");
}

/**
 * Und zurück: Was zwischen den Kommas steht, ist eine Version; was leer bleibt,
 * ist keine. Ein Feld ohne Inhalt ergibt die leere Liste und keinen leeren
 * Eintrag — der Server nähme ihn ohnehin nicht an.
 */
function versionsFromText(text: string): string[] {
  return text
    .split(",")
    .map((version) => version.trim())
    .filter((version) => version !== "");
}

function ProjectDialog({
  project,
  save,
  close,
  saved,
}: {
  project: Project | null;
  save: (draft: ProjectDraft) => Promise<Project>;
  close: () => void;
  saved: () => void;
}): ReactElement {
  const [name, setName] = useState(project?.name ?? "");
  const [svnPath, setSvnPath] = useState(project?.svnPath ?? "");
  const [currentPhpVersion, setCurrentPhpVersion] = useState(project?.currentPhpVersion ?? "");
  const [plannedPhpVersions, setPlannedPhpVersions] = useState(
    project === null ? "" : versionsAsText(project.plannedPhpVersions),
  );
  const [startRevision, setStartRevision] = useState(
    project === null ? "" : String(project.startRevision),
  );
  // `hinweis` unterdrückt nichts: Ein neues Projekt verschweigt am ersten Tag
  // nichts, das niemand eingestellt hat.
  const [displayThreshold, setDisplayThreshold] = useState<Severity>(
    project?.displayThreshold ?? "hinweis",
  );
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Zu bestätigen gibt es nur, was auch etwas kostet: eine versetzte
  // Startrevision an einem Projekt, das schon einmal abgeholt wurde.
  const moved = project !== null && Number(startRevision) !== project.startRevision;
  const needsConfirmation = moved && project.lastPollAt !== null;

  const submit = (): void => {
    if (currentPhpVersion.trim() === "") {
      setError(
        "Die laufende PHP-Version ist eine Pflichtangabe: Die Kontrolle arbeitet gegen sie "
          + "und erklärte ohne sie gültigen Code für kaputt.",
      );

      return;
    }

    if (needsConfirmation && !confirmed) {
      setError(
        "Die Startrevision zu versetzen holt alles ab dieser Revision erneut ab. "
          + "Bitte bestätigen Sie das Rücksetzen des Fortschritts.",
      );

      return;
    }

    setBusy(true);
    setError(null);

    save({
      name,
      svnPath,
      currentPhpVersion: currentPhpVersion.trim(),
      plannedPhpVersions: versionsFromText(plannedPhpVersions),
      startRevision: Number(startRevision),
      displayThreshold,
      ...(needsConfirmation ? { resetProgress: true } : {}),
    }).then(saved, (failure: unknown) => {
      setBusy(false);
      setError(describe(failure));
    });
  };

  return (
    <Modal onClose={close}>
      <Dialog
        title={project === null ? "Projekt anlegen" : "Projekt ändern"}
        description="Ein Projekt ist ein überwachter Unterbaum des Repositories. Die Startrevision hält die erste Abholung davon ab, die gesamte Historie einzuziehen."
        width={560}
        onClose={close}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={close}>
              Abbrechen
            </Button>
            <Button variant="primary" size="lg" disabled={busy} onClick={submit}>
              {busy ? "Wird gespeichert ..." : "Speichern"}
            </Button>
          </>
        }
      >
        <FormGrid labelWidth="var(--grid-label-col)">
          <FormRow label="Name" htmlFor="project-name">
            <TextField
              id="project-name"
              value={name}
              onChange={setName}
              placeholder="Kernanwendung"
            />
          </FormRow>

          <FormRow label="Überwachter Pfad" htmlFor="project-path">
            <TextField
              id="project-path"
              value={svnPath}
              onChange={setSvnPath}
              placeholder="/trunk/kern"
            />
          </FormRow>

          <FormRow label="Laufende PHP-Version" htmlFor="project-php-current">
            <TextField
              id="project-php-current"
              value={currentPhpVersion}
              onChange={setCurrentPhpVersion}
              placeholder="8.1"
            />
          </FormRow>

          <FormRow label="Geplante Zielversionen" htmlFor="project-php-planned">
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
              <TextField
                id="project-php-planned"
                value={plannedPhpVersions}
                onChange={setPlannedPhpVersions}
                placeholder="8.3, 8.4"
              />
              <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
                Durch Komma getrennt. Darf leer bleiben.
              </span>
            </div>
          </FormRow>

          <FormRow label="Startrevision" htmlFor="project-start">
            <TextField
              id="project-start"
              type="number"
              value={startRevision}
              onChange={setStartRevision}
              placeholder="4700"
            />
          </FormRow>

          <FormRow label="Anzeigeschwelle">
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
              <Select
                style={{ width: 200 }}
                value={displayThreshold}
                options={SEVERITIES}
                onSelect={(picked) =>
                  setDisplayThreshold(SEVERITIES.find((one) => one === picked) ?? "hinweis")
                }
              />
              <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
                Unterhalb dieses Grades erscheint eine Stelle unter „Befunde" nicht in der
                Vorbelegung. Erhoben und gespeichert wird trotzdem alles, und über den Filter sind
                sie wieder zu sehen. „hinweis" unterdrückt nichts.
              </span>
            </div>
          </FormRow>

          {project === null ? null : (
            <FormRow label="Fortschritt">
              <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
                {`Zuletzt verarbeitet: ${formatNumber(project.lastProcessedRevision)}`}
              </span>
            </FormRow>
          )}

          {needsConfirmation ? (
            <FormRow label="">
              <Checkbox
                checked={confirmed}
                onChange={setConfirmed}
                label={`Fortschritt zurücksetzen: alles ab Revision ${startRevision} wird erneut abgeholt und erneut begutachtet.`}
              />
            </FormRow>
          ) : null}

          {error === null ? null : (
            <FormRow full>
              <Notice text={error} glyph="triangle-alert" color="var(--ac-red)" />
            </FormRow>
          )}
        </FormGrid>
      </Dialog>
    </Modal>
  );
}
